import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Receipt, Download, Loader2, FileText, Calendar, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientInvoices = () => {
  const outletContext = useOutletContext() || {};
  const { isEmbedded = false } = outletContext;
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('clientToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: getAuthHeader()
      });
      const invoiceList = response.data.invoices || [];
      
      // Sort by paid date (Newest to Oldest) using raw ISO date for accurate sorting
      invoiceList.sort((a, b) => {
        const dateA = a.paid_at_raw || a.paid_at || a.created_at || '';
        const dateB = b.paid_at_raw || b.paid_at || b.created_at || '';
        return dateB.localeCompare(dateA); // Descending order (newest first)
      });
      
      setInvoices(invoiceList);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadInvoice = async (invoice) => {
    // Prefer Zoho invoice if available
    const invoiceId = invoice.zoho_invoice_id || invoice.id;
    
    if (!invoice.zoho_invoice_id) {
      toast.error('Invoice not available for download. Please contact support.');
      return;
    }
    
    setDownloadingInvoice(invoice.id);
    try {
      toast.loading('Downloading invoice...', { id: 'download' });
      
      // Download from Zoho
      const response = await axios.get(
        `${API}/invoice/${invoice.zoho_invoice_id}/pdf`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Use invoice number for filename if available
      const filename = invoice.zoho_invoice_number 
        ? `Invoice_${invoice.zoho_invoice_number}.pdf`
        : `Invoice_${invoiceId}.pdf`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!', { id: 'download' });
    } catch (error) {
      console.error('Failed to download invoice:', error);
      
      let errorMsg = 'Failed to download invoice. Please try again.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // Could not parse error
        }
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      toast.error(errorMsg, { id: 'download' });
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const formatDate = (dateString) => {
    // Backend already returns the date formatted in IST (e.g., "20 Jan 2026, 02:18 PM IST")
    // So we just display it directly
    if (!dateString) return 'N/A';
    return dateString;
  };

  const getPaymentTypeLabel = (type) => {
    switch (type) {
      case 'will_creation':
        return 'Will Creation';
      case 'will_renewal':
        return 'Will Renewal';
      default:
        return 'Payment';
    }
  };

  const getPaymentTypeColor = (type) => {
    switch (type) {
      case 'will_creation':
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
      case 'will_renewal':
        return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
      default:
        return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="loading">
        <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
        <p className="text-slate-400">Loading your invoices...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="client-invoices">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Invoices</h1>
        <p className="text-slate-400 mt-1">View and download your payment invoices</p>
      </div>

      {/* Invoices List or Empty State */}
      {invoices.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-6 rounded-full bg-slate-800/50">
                <Receipt className="w-16 h-16 text-slate-600" />
              </div>
              <h2 className="text-2xl font-bold text-white">No Invoices Found</h2>
              <p className="text-slate-400 max-w-md">
                You do not have any invoices yet. Invoices are generated when you make a payment for will creation or renewal.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <Card 
              key={invoice.id} 
              className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all"
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Invoice Info */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-amber-500/10">
                      <Receipt className="w-6 h-6 text-amber-400" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white">
                          ₹{invoice.amount?.toLocaleString('en-IN')}
                        </h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPaymentTypeColor(invoice.payment_type)}`}>
                          {getPaymentTypeLabel(invoice.payment_type)}
                        </span>
                        {invoice.status === 'refunded' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-red-500/10 text-red-400 border-red-500/30">
                            Refunded
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(invoice.paid_at)}
                        </span>
                        {invoice.partner_name && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            via {invoice.partner_name}
                          </span>
                        )}
                        {invoice.payment_id && (
                          <span className="flex items-center gap-1 font-mono text-xs">
                            <CreditCard className="w-4 h-4" />
                            {invoice.payment_id.substring(0, 20)}...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-auto">
                    {invoice.zoho_invoice_id ? (
                      <Button
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                        onClick={() => handleDownloadInvoice(invoice)}
                        disabled={downloadingInvoice === invoice.id}
                        data-testid={`download-invoice-${invoice.id}`}
                      >
                        {downloadingInvoice === invoice.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </>
                        )}
                      </Button>
                    ) : (
                      <span className="text-slate-500 text-sm">Invoice not available</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white text-lg">About Invoices</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium text-white">Invoice Delivery</h4>
              <p className="text-slate-400">
                Invoices are automatically sent to your registered email address after each successful payment.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-white">Download & Print</h4>
              <p className="text-slate-400">
                Click Download to get a PDF copy of your invoice for your records or tax purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientInvoices;
